import React, {
  Fragment,
  ReactElement,
  ComponentType,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  ForwardedRef,
} from "react";
import { _cs, randomString } from "@togglecorp/fujs";

export interface ModalizeAddedProps {
  disabled?: boolean;
  modal: ReactElement<{
    closeModal?: (...args: unknown[]) => void;
    parentBCR?: DOMRectReadOnly;
  }>;
  className?: string;
  initialShowModal?: boolean;
  onClose?: (...args: unknown[]) => void;
  transparent?: boolean;
}

function modalize<WProps extends object>(
  WrappedButton: ComponentType<WProps & { ref?: ForwardedRef<HTMLDivElement> }>
) {
  type Props = WProps & ModalizeAddedProps;

  const Modalized = React.forwardRef<HTMLDivElement, Props>(
    (
      {
        disabled = false,
        modal,
        className: classNameFromProps,
        initialShowModal = false,
        onClose,
        ...wrappedProps
      },
      forwardedRef
    ) => {
      const [showModal, setShowModal] = useState(initialShowModal);
      const [buttonBCR, setButtonBCR] = useState<DOMRectReadOnly | undefined>();

      const buttonClass = useMemo(() => randomString(16), []);
      const buttonRef = useRef<HTMLDivElement>(null);

      const updateBCR = useCallback(() => {
        const el = document.getElementsByClassName(buttonClass)[0] as
          | HTMLElement
          | undefined;
        if (el) setButtonBCR(el.getBoundingClientRect());
      }, [buttonClass]);

      useEffect(() => {
        updateBCR();
        if (!showModal) return;

        const onResize = () => updateBCR();
        const onScroll = () => updateBCR();

        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);

        return () => {
          window.removeEventListener("resize", onResize);
          window.removeEventListener("scroll", onScroll, true);
        };
      }, [showModal, updateBCR]);

      /* -------------------------------------------------------------- */
      /* Handlers                                                        */
      /* -------------------------------------------------------------- */
      const open = () => {
        updateBCR();
        setShowModal(true);
      };

      const close = (...args: unknown[]) => {
        setShowModal(false);
        onClose?.(...args);
      };

      const finalClass = _cs(classNameFromProps, buttonClass);

      return (
        <Fragment>
          <WrappedButton
            {...(wrappedProps as WProps)}
            disabled={disabled || showModal}
            onClick={open}
            className={finalClass}
            ref={(el) => {
              buttonRef.current = el;
              if (typeof forwardedRef === "function") forwardedRef(el);
              else if (forwardedRef) forwardedRef.current = el;
            }}
          />
          {showModal &&
            React.cloneElement(modal, {
              closeModal: close,
              parentBCR: buttonBCR,
            })}
        </Fragment>
      );
    }
  );

  Modalized.displayName = `Modalized(${
    WrappedButton.displayName ?? WrappedButton.name ?? "Component"
  })`;

  return Modalized;
}

export default modalize;
